import { useEffect, useMemo, useState } from "react";
import { DragIcon } from "~/assets/icons";
import styles from "./EstimateLayoutStep4.module.scss";
import classNames from "classnames/bind";
import FormInput from "~/components/common/form/formInput/FormInput";
import { PlusIcon } from "lucide-react";
import AddItem from "~/components/common/add-item/AddItem";
import DroppableArea from "~/components/common/drop-drag/DroppableArea";
import TitleEditor from "~/components/common/form/titleEdit/TitleEditor";
import BaseBoxContent from "~/components/common/base-box-content/BaseBoxContent";
import { Divider } from "@mui/material";
import BaseSelectCustom from "~/components/common/base-select-custom/BaseSelectCustom";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import { EstimateUoMOption } from "~/pages/admin/jobs/add-job/constants/add-estimate.constants";
import type {
    EstimateLayoutStep4FormData as Step4FormData,
    IOwenStep4FormData,
    ISectionStep4FormData,
    ILineStep4FormData,
} from "~/utils/interface/estimateSetting";
import { Trash2 } from "lucide-react";
import OutlinedButton from "~/components/common/outlined-button/OutlinedButton";

const cx = classNames.bind(styles);

const DEFAULT_LINE: ILineStep4FormData = {
    itemName: "",
    type: "MATERIAL",
    uom: "",
    quantity: 0,
    price: 0,
};

const DEFAULT_SECTION: ISectionStep4FormData = {
    title: "Section 1",
    lines: [{ ...DEFAULT_LINE }],
};

const DEFAULT_OWEN: IOwenStep4FormData = {
    title: "Owen 1",
    sections: [{ ...DEFAULT_SECTION }],
    quoteProfitMargin: 0,
    note: "",
};

interface EstimateLayoutStep4Props {
    onStep4DataChange?: (data: Step4FormData) => void;
    initialData?: Step4FormData | null;
}

const EstimateLayoutStep4 = ({ onStep4DataChange, initialData }: EstimateLayoutStep4Props) => {
    const DEFAULT_TITLE_STEP = "Quote Details";
    const [formData, setFormData] = useState<Step4FormData>(() => {
        const data = initialData ?? undefined;
        if (!data?.owens?.length) {
            return {
                titleStep4: data?.titleStep4 ?? DEFAULT_TITLE_STEP,
                owens: [{ ...DEFAULT_OWEN }],
            };
        }
        return {
            titleStep4: data.titleStep4 ?? DEFAULT_TITLE_STEP,
        };
    });

    const [targetTab, setTargetTab] = useState(0);

    const notifyParent = (data: Step4FormData) => {
        setFormData(data);
        onStep4DataChange?.(data);
    };

    const handleUpdateTitle = (update: Partial<Step4FormData>) => {
        const data: Step4FormData = { ...formData, ...update };
        notifyParent(data);
    };

    const handleUpdateOwen = (update: Partial<IOwenStep4FormData>, index: number) => {
        const owens = [...(formData.owens ?? [])];
        owens[index] = { ...owens[index], ...update };
        notifyParent({ ...formData, owens });
    };

    const handleUpdateSection = (update: Partial<ISectionStep4FormData>, sectionIndex: number) => {
        const owens = [...(formData.owens ?? [])];
        const owen = owens[targetTab];
        if (!owen?.sections) return;
        owen.sections[sectionIndex] = { ...owen.sections[sectionIndex], ...update };
        notifyParent({ ...formData, owens });
    };

    const handleSectionLineUpdate = (
        update: Partial<ILineStep4FormData>,
        sectionIndex: number,
        lineIndex: number
    ) => {
        const owens = [...(formData.owens ?? [])];
        const section = owens[targetTab]?.sections?.[sectionIndex];
        if (!section?.lines) return;
        section.lines[lineIndex] = { ...section.lines[lineIndex], ...update };
        notifyParent({ ...formData, owens });
    };

    const removeOwen = (index: number) => {
        const owens = formData.owens ?? [];
        if (owens.length <= 1) return;
        const next = owens.filter((_, i) => i !== index);
        if (targetTab === index) setTargetTab(0);
        else if (targetTab > index) setTargetTab(targetTab - 1);
        notifyParent({ ...formData, owens: next });
    };

    const handleRemoveSection = (sectionIndex: number) => {
        const owens = [...(formData.owens ?? [])];
        const owen = owens[targetTab];
        if (!owen?.sections?.length) return;
        owen.sections.splice(sectionIndex, 1);
        notifyParent({ ...formData, owens });
    };

    const handleRemoveLine = (sectionIndex: number, lineIndex: number) => {
        const owens = [...(formData.owens ?? [])];
        const section = owens[targetTab]?.sections?.[sectionIndex];
        if (!section?.lines) return;
        section.lines.splice(lineIndex, 1);
        notifyParent({ ...formData, owens });
    };

    const handleAddOwen = () => {
        const owens = [...(formData.owens ?? [])];
        owens.push({
            ...DEFAULT_OWEN,
            title: `Owen ${owens.length + 1}`,
        });
        setTargetTab(owens.length - 1);
        notifyParent({ ...formData, owens });
    };

    const handleAddSection = () => {
        const owens = [...(formData.owens ?? [])];
        const owen = owens[targetTab];
        if (!owen) return;
        const sections = [...(owen.sections ?? [])];
        sections.push({
            title: `Section ${sections.length + 1}`,
            lines: [{ ...DEFAULT_LINE }],
        });
        owens[targetTab] = { ...owen, sections };
        notifyParent({ ...formData, owens });
    };

    const handleAddLine = (sectionIndex: number) => {
        const owens = [...(formData.owens ?? [])];
        const owen = owens[targetTab];
        const section = owen?.sections?.[sectionIndex];
        if (!owen?.sections || !section) return;
        const lines = [...(section.lines ?? [])];
        lines.push({ ...DEFAULT_LINE });
        owen.sections[sectionIndex] = { ...section, lines };
        notifyParent({ ...formData, owens });
    };

    const handleDropSection = (newSections: ISectionStep4FormData[]) => {
        const owens = [...(formData.owens ?? [])];
        if (!owens[targetTab]) return;
        owens[targetTab] = { ...owens[targetTab], sections: newSections };
        notifyParent({ ...formData, owens });
    };

    const handleDropLines = (sectionIndex: number, newLines: ILineStep4FormData[]) => {
        const owens = [...(formData.owens ?? [])];
        const owen = owens[targetTab];
        const section = owen?.sections?.[sectionIndex];
        if (!owen?.sections || !section) return;
        owen.sections[sectionIndex] = { ...section, lines: newLines };
        notifyParent({ ...formData, owens });
    };

    const totalSection = (lines: ILineStep4FormData[] = []) => {
        return lines
            .reduce((sum, line) => sum + (Number(line.quantity) || 0) * (Number(line.price) || 0), 0)
            .toFixed(2);
    };

    useEffect(() => {
        const data = initialData ?? undefined;
        if (data?.owens?.length) {
            setFormData({
                titleStep4: data.titleStep4 ?? "",
                owens: data.owens,
            });
        }
    }, [initialData]);

    const owens = formData.owens ?? [];
    const currentOwen = owens[targetTab];
    const sections = currentOwen?.sections ?? [];

    const breakdown = useMemo(() => {
        const owen = formData.owens?.[targetTab];
        const secs = owen?.sections ?? [];
        let _mainTotal = 0;
        let _material = 0;
        let _labor = 0;
        secs.forEach((section) => {
            (section.lines ?? []).forEach((line) => {
                const itemTotal = (Number(line.quantity) || 0) * (Number(line.price) || 0);
                _mainTotal += itemTotal;
                if (line.type === "LABOR") {
                    _labor += itemTotal;
                } else {
                    _material += itemTotal;
                }
            });
        });
        const _subTotal = _mainTotal;
        const progress = Math.max(0, Math.min(0.99, Number(owen?.quoteProfitMargin) || 0));
        const _profit = _subTotal * progress;
        const _total = _subTotal + _profit;
        const _cogs = _material + _labor;
        return {
            subTotal: _subTotal,
            total: _total,
            material: _material,
            labor: _labor,
            cogs: _cogs,
            profit: _profit,
            marginPercent: progress * 100,
        };
    }, [formData, targetTab]);

    if (!owens.length) return null;

    return (
        <div className={cx("estimate-layout-step-4-container")}>
            <div className={cx("header")}>
                <TitleEditor
                    value={formData.titleStep4 ?? ""}
                    name=""
                    placeholder=""
                    inputkey=""
                    isViewMode={false}
                    onChange={(value) => handleUpdateTitle({ titleStep4: value })}
                    styleView={{ fontSize: "20px", fontWeight: 700 }}
                />
                <div className={cx("button-container")}>
                    <PrimaryButton title="View Page" onClick={() => { }} className={cx("btn-preview")} />
                </div>
            </div>

            <div className={cx("content")}>
                <div className={cx("quotes")}>
                    {owens.map((owen, index) => (
                        <div
                            key={index}
                            className={cx({ selected: index === targetTab, "quote-title": true })}
                            onClick={(e) => {
                                setTargetTab(index);
                                e.stopPropagation();
                            }}
                        >
                            <button
                                type="button"
                                onClick={(e) => {
                                    removeOwen(index);
                                    e.stopPropagation();
                                }}
                            >
                                <svg
                                    width="28"
                                    height="28"
                                    viewBox="0 0 28 28"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M20 8L8 20M8 8L20 20"
                                        stroke="#FA6363"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>
                            <TitleEditor
                                value={owen.title ?? ""}
                                name=""
                                placeholder=""
                                inputkey=""
                                isViewMode={false}
                                onChange={(value) => handleUpdateOwen({ title: value }, index)}
                                styleView={{ fontSize: "16px", fontWeight: 500 }}
                            />
                        </div>
                    ))}
                    <div onClick={handleAddOwen} role="button" tabIndex={0} onKeyDown={() => { }}>
                        <PlusIcon color="#ffffff" />
                    </div>
                </div>

                {currentOwen && (
                    <>
                        <DroppableArea
                            data={sections}
                            onChange={(newArray: ISectionStep4FormData[]) => handleDropSection(newArray)}
                            groupName="sections"
                            renderComponent={(section: ISectionStep4FormData, sectionIndex: number) => (
                                <BaseBoxContent key={sectionIndex} className={cx("section-item")} expanded>
                                    <div data-slot="header">
                                        <div className={cx("section-title")}>
                                            <div className={cx("drag-button")}>
                                                <DragIcon className={cx("drag-icon")} />
                                            </div>
                                            <div className="flex items-center justify-between w-full">
                                                <TitleEditor
                                                    value={section.title ?? ""}
                                                    name=""
                                                    placeholder=""
                                                    inputkey=""
                                                    isViewMode={false}
                                                    onChange={(value) =>
                                                        handleUpdateSection({ title: value }, sectionIndex)
                                                    }
                                                    styleView={{ fontSize: "20px", fontWeight: 700 }}
                                                />
                                                <button
                                                    type="button"
                                                    className="w-[24px] h-[24px] cursor-pointer"
                                                    onClick={() => handleRemoveSection(sectionIndex)}
                                                >
                                                    <Trash2 color="#FA6363" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div data-slot="content">
                                        <div className={cx("section-item-table")}>
                                            <div className={cx("section-item-header")}>
                                                <div className="w-[24px]" />
                                                <div className="w-[36%]">
                                                    <h3>Item Name</h3>
                                                </div>
                                                <div className="w-[14%]">
                                                    <h3>UoM</h3>
                                                </div>
                                                <div className="w-[12%]">
                                                    <h3>Quantity</h3>
                                                </div>
                                                <div className="w-[12%]">
                                                    <h3>Price</h3>
                                                </div>
                                                <div className="w-[16%]">
                                                    <h3>Line Total</h3>
                                                </div>
                                                <div className="w-[5%]" />
                                            </div>
                                            <div className={cx("section-item-table-body")}>
                                                <DroppableArea
                                                    data={section.lines ?? []}
                                                    groupName={`sectionItems-${sectionIndex}`}
                                                    onChange={(newLines: ILineStep4FormData[]) =>
                                                        handleDropLines(sectionIndex, newLines)
                                                    }
                                                    renderComponent={(line: ILineStep4FormData, lineIndex: number) => (
                                                        <div key={lineIndex} className={cx("section-item-table-row")}>
                                                            <div className={cx("drag-button")}>
                                                                <DragIcon className={cx("drag-icon")} />
                                                            </div>
                                                            <div className="w-[36%]">
                                                                <FormInput
                                                                    name="itemName"
                                                                    text=""
                                                                    isViewMode={false}
                                                                    isViewNoInput
                                                                    placeholder=""
                                                                    inputkey="itemName"
                                                                    value={line.itemName ?? ""}
                                                                    onChange={(e) =>
                                                                        handleSectionLineUpdate(
                                                                            { itemName: e.target.value },
                                                                            sectionIndex,
                                                                            lineIndex
                                                                        )
                                                                    }
                                                                    height="31px"
                                                                />
                                                            </div>
                                                            <div className="w-[14%]">
                                                                <BaseSelectCustom
                                                                    selectedView={line.uom ?? ""}
                                                                    onViewChange={(viewId) =>
                                                                        handleSectionLineUpdate(
                                                                            { uom: viewId != null ? String(viewId) : undefined },
                                                                            sectionIndex,
                                                                            lineIndex
                                                                        )
                                                                    }
                                                                    height="36px"
                                                                    isViewMode={false}
                                                                    isViewNoInput
                                                                    viewOptions={EstimateUoMOption}
                                                                    placeholder="---"
                                                                    className={cx("select-filter")}
                                                                    minWidth="100px"
                                                                    textAlign="left"
                                                                    IconDrop={
                                                                        <div className={cx("drop")}>
                                                                            <svg
                                                                                width="24"
                                                                                height="24"
                                                                                viewBox="0 0 24 24"
                                                                                fill="none"
                                                                                xmlns="http://www.w3.org/2000/svg"
                                                                            >
                                                                                <path
                                                                                    d="M11.9994 14.308L8.19141 10.5H15.8074L11.9994 14.308Z"
                                                                                    fill="black"
                                                                                />
                                                                            </svg>
                                                                        </div>
                                                                    }
                                                                />
                                                            </div>
                                                            <div className="w-[12%]">
                                                                <FormInput
                                                                    name="number"
                                                                    text=""
                                                                    value={line.quantity ?? ""}
                                                                    height="31px"
                                                                    isViewMode={false}
                                                                    isViewNoInput
                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                                        handleSectionLineUpdate(
                                                                            { quantity: Number(e.target.value) || 0 },
                                                                            sectionIndex,
                                                                            lineIndex
                                                                        )
                                                                    }
                                                                    inputkey=""
                                                                    placeholder=""
                                                                />
                                                            </div>
                                                            <div className="w-[12%]">
                                                                <FormInput
                                                                    name="company"
                                                                    text=""
                                                                    isViewMode={false}
                                                                    isViewNoInput
                                                                    placeholder=""
                                                                    inputkey="company"
                                                                    value={line.price ?? ""}
                                                                    onChange={(e) =>
                                                                        handleSectionLineUpdate(
                                                                            {
                                                                                price: Number(e.target.value) || 0,
                                                                            },
                                                                            sectionIndex,
                                                                            lineIndex
                                                                        )
                                                                    }
                                                                    height="31px"
                                                                />
                                                            </div>
                                                            <div className="w-[16%]">
                                                                <div className={cx("total-money")}>
                                                                    <h3>
                                                                        $
                                                                        {(
                                                                            (Number(line.quantity) || 0) *
                                                                            (Number(line.price) || 0)
                                                                        ).toFixed(2)}
                                                                    </h3>
                                                                </div>
                                                            </div>
                                                            <div className="w-[5%] flex items-center">
                                                                <div
                                                                    className={cx("text-delete")}
                                                                    role="button"
                                                                    tabIndex={0}
                                                                    onClick={() =>
                                                                        handleRemoveLine(sectionIndex, lineIndex)
                                                                    }
                                                                    onKeyDown={() => { }}
                                                                >
                                                                    <svg
                                                                        width="16"
                                                                        height="16"
                                                                        viewBox="0 0 16 16"
                                                                        fill="none"
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                    >
                                                                        <path
                                                                            d="M3.33398 3.33398L12.6673 12.6673"
                                                                            stroke="#FA6363"
                                                                            strokeWidth="1.5"
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                        />
                                                                        <path
                                                                            d="M3.33269 12.6673L12.666 3.33398"
                                                                            stroke="#FA6363"
                                                                            strokeWidth="1.5"
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                        />
                                                                    </svg>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div data-slot="footer">
                                        <div className={cx("section-action")}>
                                            <AddItem onClick={() => handleAddLine(sectionIndex)} />
                                        </div>
                                        <div className={cx("section-item-footer")}>
                                            <div className={cx("total")}>
                                                <h4>Section Total:</h4>
                                                <h3>${totalSection(section.lines)}</h3>
                                            </div>
                                        </div>
                                    </div>
                                </BaseBoxContent>
                            )}
                        />
                    </>
                )}

                <div className="flex items-center justify-between">
                    <Divider className="w-[calc(50%-150px)] !bg-[#1A2B47] !border-b-[#1A2B47]" />
                    <button type="button" className={cx("add-section")} onClick={handleAddSection}>
                        Add Section
                    </button>
                    <Divider className="w-[calc(50%-150px)] !bg-[#1A2B47] !border-b-[#1A2B47]" />
                </div>

                {currentOwen && (
                    <div className={cx("step4-owen-block")}>
                        <div className={cx("step4-owen-summary")}>
                            <p className={cx("step4-owen-summary-title")}>Estimate Summary</p>
                            <div className={cx("step4-owen-summary-box")}>
                                <div className={cx("step4-owen-summary-row")}>
                                    <span className={cx("step4-owen-summary-label")}>Estimate subtotal</span>
                                    <span className={cx("step4-owen-summary-value")}>$ {breakdown.subTotal.toFixed(2)}</span>
                                </div>
                                <Divider className={cx("step4-owen-summary-divider")} />
                                <div className={cx("step4-owen-summary-row", "step4-owen-summary-row-total")}>
                                    <span className={cx("step4-owen-summary-label")}>Total</span>
                                    <span className={cx("step4-owen-summary-value-total")}>$ {breakdown.total.toFixed(2)}</span>
                                </div>
                            </div>

                            <div className={cx("step4-owen-breakdown")}>
                                <div className={cx("step4-owen-breakdown-title")}>
                                    <h2>Total Breakdown</h2>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M17 11L12 6L7 11M17 18L12 13L7 18" stroke="#F9FBFE" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                                <div className={cx("step4-owen-breakdown-cards")}>
                                    <div className={cx("step4-owen-breakdown-card")}>
                                        <h4>Material</h4>
                                        <h2>${breakdown.material.toFixed(2)}</h2>
                                    </div>
                                    <div className={cx("step4-owen-breakdown-card")}>
                                        <h4>Labor</h4>
                                        <h2>${breakdown.labor.toFixed(2)}</h2>
                                    </div>
                                    <div className={cx("step4-owen-breakdown-card")}>
                                        <h4>COCS</h4>
                                        <h2>${breakdown.cogs.toFixed(2)}</h2>
                                    </div>
                                    <div className={cx("step4-owen-breakdown-card")}>
                                        <h4>Margin ({breakdown.marginPercent.toFixed(0)}%)</h4>
                                        <h2>${(breakdown.subTotal * (breakdown.marginPercent / 100)).toFixed(2)}</h2>
                                    </div>
                                    <div className={cx("step4-owen-breakdown-card")}>
                                        <h4>Profit</h4>
                                        <h2>${breakdown.profit.toFixed(2)}</h2>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={cx("step4-owen-notes")}>
                            <p className={cx("step4-owen-notes-title")}>Notes</p>
                            <div className={cx("step4-owen-notes-input")}>
                                <FormInput
                                    name="note"
                                    text=""
                                    placeholder="Notes"
                                    inputkey="note"
                                    value={currentOwen.note ?? undefined}
                                    isViewMode={false}
                                    inputType="textarea"
                                    height="121px"
                                    onChange={(e) =>
                                        handleUpdateOwen({ note: e.target.value }, targetTab)
                                    }
                                />
                            </div>
                        </div>

                        <OutlinedButton
                            title="Save As Template"
                            onClick={() => { }}
                            className={cx("save-as-template-button")}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default EstimateLayoutStep4;